import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import CountUp from 'react-countup';
import ServiceDetail from './ServiceDetail';
import {
  Brain, Smartphone, Globe, Cloud, Layers, Palette,
  Shield, Database, Cpu, Code2, BarChart2, Settings2
} from 'lucide-react';
import aiServiceIcon from '../assets/ai_service_icon.png';
import mobileServiceIcon from '../assets/mobile_service_icon.png';
import webServiceIcon from '../assets/web_service_icon.png';
import cloudServiceIcon from '../assets/cloud_service_icon.png';
import cyberServiceIcon from '../assets/cyber_service_icon.png';
import dataServiceIcon from '../assets/data_service_icon.png';
import iotServiceIcon from '../assets/iot_service_icon.png';
import marketingServiceIcon from '../assets/marketing_service_icon.png';
import uiServiceIcon from '../assets/ui_service_icon.png';

const services = [
  {
    bgImage: aiServiceIcon,
    title: 'AI & Machine Learning',
    desc:  'End-to-end AI solutions — chatbots, predictive analytics, computer vision, NLP, and intelligent automation built for real-world impact.',
    color: 'from-blue-500 to-indigo-600',
  },
  {
    bgImage: mobileServiceIcon,
    title: 'Mobile App Development',
    desc:  'Cross-platform and native apps for iOS and Android — sleek, performant, and scalable from MVP to enterprise-grade solutions.',
    color: 'from-orange-400 to-orange-600',
  },
  {
    bgImage: webServiceIcon,
    title: 'Web Development',
    desc:  'Full-stack web applications, e-commerce platforms, portals, and PWAs crafted with modern frameworks and best-in-class UX.',
    color: 'from-sky-400 to-blue-600',
  },
  {
    bgImage: cloudServiceIcon,
    title: 'Cloud & DevOps',
    desc:  'Cloud architecture, CI/CD pipelines, containerisation, and infrastructure automation to keep your systems fast and reliable.',
    color: 'from-violet-500 to-purple-600',
  },
  {
    bgImage: uiServiceIcon,
    title: 'UI/UX Design',
    desc:  'Research-driven design systems, wireframes, prototypes, and pixel-perfect interfaces that delight users and drive conversions.',
    color: 'from-pink-500 to-rose-500',
  },
  {
    bgImage: cyberServiceIcon,
    title: 'Cybersecurity & QA',
    desc:  'Comprehensive security audits, penetration testing, automated QA, and quality assurance to ensure robust, safe software.',
    color: 'from-emerald-500 to-teal-600',
  },
  {
    bgImage: dataServiceIcon,
    title: 'Data Engineering',
    desc:  'Data pipelines, warehousing, dashboards, and BI tools that turn raw data into actionable business intelligence.',
    color: 'from-amber-500 to-yellow-500',
  },
  {
    bgImage: iotServiceIcon,
    title: 'Embedded & IoT',
    desc:  'Smart device software, IoT platforms, firmware, and real-time systems that bridge the physical and digital worlds.',
    color: 'from-cyan-500 to-blue-500',
  },
  {
    bgImage: marketingServiceIcon,
    title: 'Digital Marketing Tech',
    desc:  'SEO-optimised platforms, marketing automation tools, CRM integrations, and performance analytics dashboards.',
    color: 'from-blue-500 to-indigo-600',
  },
];

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.10 } }
};
const cardVariants = {
  hidden:   { y: 50, opacity: 0 },
  visible:  { y: 0,  opacity: 1, transition: { duration: 0.55, ease: [0.25, 0.46, 0.45, 0.94] } }
};

function ServiceCard({ service, index, onExplore }) {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  
  return (
    <motion.div
      ref={ref}
      variants={cardVariants}
      initial="hidden"
      animate={inView ? 'visible' : 'hidden'}
      transition={{ delay: index * 0.08 }}
      className="service-card group relative rounded-3xl overflow-hidden h-[360px] shadow-card border border-slate-200/50 hover:border-slate-300"
    >
      {/* Background Image or Gradient */}
      {service.bgImage ? (
        <div className="absolute inset-0 z-0">
          <img src={service.bgImage} alt={service.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
          <div className="absolute inset-0 bg-slate-900/40 group-hover:bg-slate-900/20 transition-colors duration-500" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-900/90 to-transparent" />
        </div>
      ) : (
        <div className="absolute inset-0 z-0 bg-slate-900 overflow-hidden">
           <div className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-20 group-hover:opacity-30 transition-opacity duration-500`} />
           {/* Giant faded icon */}
           <div className="absolute -bottom-10 -right-10 text-white/5 transition-transform duration-700 group-hover:scale-110 group-hover:-rotate-12">
              {React.cloneElement(service.icon, { size: 240 })}
           </div>
           <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent" />
        </div>
      )}

      {/* Content */}
      <div className="relative z-10 p-8 h-full flex flex-col justify-end">
        {/* If no bgImage, show the small icon at the top */}
        {!service.bgImage && (
           <div className="absolute top-8 left-8 w-14 h-14 rounded-2xl bg-white/10 backdrop-blur-md flex items-center justify-center border border-white/10 text-white mb-auto shadow-xl">
             {service.icon}
           </div>
        )}

        <h3 className="font-syne font-bold text-2xl text-white mb-3 tracking-wide">{service.title}</h3>
        <p className="font-inter text-sm text-slate-300 leading-relaxed mb-6 opacity-80 group-hover:opacity-100 transition-opacity duration-300 line-clamp-3">
          {service.desc}
        </p>

        <div
          onClick={() => onExplore(service)}
          className="flex items-center gap-2 text-white font-space font-semibold text-sm opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-2 group-hover:translate-y-0 cursor-pointer"
        >
          Explore Solution
          <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 18l6-6-6-6"/></svg>
        </div>
      </div>
      
      {/* Hover glow line */}
      <div className={`absolute bottom-0 left-0 right-0 h-1.5 bg-gradient-to-r ${service.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
    </motion.div>
  );
}

export default function Services() {
  const [titleRef, titleInView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const [selectedService, setSelectedService] = useState(null);

  return (
    <>
      {selectedService && (
        <ServiceDetail service={selectedService} onClose={() => setSelectedService(null)} />
      )}
    <section id="services" className="py-28 bg-white relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-brand-blue/3 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-brand-orange/4 blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div
          ref={titleRef}
          initial={{ y: 30, opacity: 0 }}
          animate={titleInView ? { y: 0, opacity: 1 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <span className="section-label mb-5 inline-block">What We Do</span>
          <h2 className="font-syne font-extrabold text-4xl md:text-5xl text-slate-900 mb-5">
            Full-Spectrum <span className="gradient-text-blue">Technology</span> Services
          </h2>
          <p className="font-inter text-slate-500 text-lg max-w-2xl mx-auto leading-relaxed">
            From ideation to deployment, we deliver end-to-end digital solutions across
            every technology domain — tailored precisely to your vision.
          </p>
        </motion.div>

        {/* Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {services.map((service, i) => (
            <ServiceCard key={i} service={service} index={i} onExplore={setSelectedService} />
          ))}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="text-center mt-14"
        >
          <p className="font-inter text-slate-500 mb-4">Have a unique requirement?</p>
          <a href="#contact">
            <button className="btn-primary">Discuss Your Project</button>
          </a>
        </motion.div>
      </div>
      </section>
    </>
  );
}
